import {
  ComplianceAutoApprovalAttemptDecision,
  ExternalIngestionDocumentFragment,
  FieldType,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  RuleType,
  VariableSource,
} from '@client/graphql/__generated__/types';
import { MultiFieldValue } from '@common/graphql';
import {
  getContractDocumentPageCount,
  getContractDocumentPageOrder,
  getContractDocumentPageRange,
  getPageNumbers,
  toDocumentRelativePages,
} from '@common/utils/validation';
import {
  validationFailureExplanationSchema,
  type ValidationFailureExplanation,
} from '@common/utils/validation-failure-explanation';
import type { ValidationFailureMessage } from '@common/utils/validation-failure-message';
import {
  validationFieldHighlightsSchema,
  type ValidationFieldHighlights,
} from '@common/utils/validation-field-highlights';
import * as Sentry from '@sentry/react';
import { groupBy, memoize, partition } from 'lodash';
import { TITLE_TERMINATORS, titleCase } from 'title-case';
import { match } from 'ts-pattern';
import { z } from 'zod';
import type {
  ComplianceIngestionDocumentValidation,
  ComplianceValidationReportData,
} from '../Compliance.types';
import { getFieldLevelComparisonData } from './fieldComparison.utils';
import {
  getValidationItemFieldIds,
  parseFieldsByContractDocument,
  type FieldsByContractDocument,
} from './fieldHighlights.utils';
import { createFieldKeyToLabelMapping } from './fieldMapping.utils';

export interface ValidationItem
  extends ValidationFieldHighlights,
    ValidationFailureExplanation {
  message: string;
  ruleUuid: string;
  ruleType?: RuleType;
  /**
   * Failing pages as ABSOLUTE pages in the ingestion document's combined PDF.
   * Keep using these for anything that indexes the rendered PDF — field lookups,
   * overlays, scroll targets — since those are all keyed on absolute pages.
   */
  pageNumbers?: number[];
  /**
   * The same pages renumbered relative to this item's contract document, for
   * DISPLAY only: a combined PDF should tell the user "Page 3" of the addendum,
   * not "Page 25" of the bundle (BLO-13411). Falls back to the absolute pages
   * when the document's page range isn't known.
   */
  documentPageNumbers?: number[];
  /**
   * Page count of this item's contract document alone (not the whole combined
   * PDF), so single-page documents can drop the page prefix. `0` when unknown.
   */
  documentPageCount?: number;
  contractDocumentUuid?: string;
  mappingKeys?: string[];
  ingestionDocumentValidationId?: string;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  isOrganizationRule?: boolean;
  isOrganizationIgnored?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
}

export interface ValidationResult extends ValidationFailureMessage {
  ruleUuid: string;
  output: boolean;
  message: string;
  ruleType?: RuleType;
  /** @deprecated legacy single-page; read via getPageNumbers(). */
  pageNumber?: number;
  pageNumbers?: number[];
  contractDocumentUuid?: string;
  fieldMappingKey?: string;
  focusFieldMappingKeys?: string[] | null;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  isOrganizationRule?: boolean;
  isOrganizationIgnored?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
  documentVersionFieldUuid?: string;
}

export interface ValidationResultsForDocument {
  errors: ValidationItem[];
  successes: ValidationItem[];
  organizationIgnoredRules?: ValidationItem[];
}

/**
 * The lowest ABSOLUTE page a validation item is attributed to. Used to look up
 * field geometry (which is keyed on absolute pages), NOT as a sort key — see
 * {@link sortValidationItemsByPage}. Items with no page attribution return
 * +Infinity.
 */
function getFirstPageNumber(item: ValidationItem): number {
  return item.pageNumbers?.length
    ? Math.min(...item.pageNumbers)
    : Number.POSITIVE_INFINITY;
}

/**
 * The lowest DOCUMENT-RELATIVE page a validation item is attributed to — the
 * page the item actually renders (BLO-13411) — so the list reads in the same
 * order as the page numbers on screen. Falls back to the absolute pages when
 * the item's contract document has no known page range, which is exactly what
 * `documentPageNumbers` already degrades to.
 */
function getFirstDocumentPageNumber(item: ValidationItem): number {
  const pages = item.documentPageNumbers?.length
    ? item.documentPageNumbers
    : item.pageNumbers;

  return pages?.length ? Math.min(...pages) : Number.POSITIVE_INFINITY;
}

/**
 * An item's contract document's position in the combined PDF, used as the
 * primary sort key so items group document by document. Items whose contract
 * document isn't in the order map (unknown page range, or a fragment that
 * doesn't select `contractDocumentPages`) sort last; when NO document is in the
 * map they all tie, and ordering falls through to the page keys — i.e. the
 * pre-grouping behaviour.
 */
function getContractDocumentSortValue(
  item: ValidationItem,
  contractDocumentOrder: Map<string, number>
): number {
  if (!item.contractDocumentUuid) return Number.POSITIVE_INFINITY;

  return (
    contractDocumentOrder.get(item.contractDocumentUuid) ??
    Number.POSITIVE_INFINITY
  );
}

interface FieldPosition {
  pageNumber: number;
  /** Normalized, top-origin vertical offset — smaller is higher on the page. */
  y: number;
}

/** `contractDocumentUuid` → `mappingKey` → field positions on each page. */
type FieldPositionLookup = Map<string, Map<string, FieldPosition[]>>;

/**
 * Builds a lookup of field geometry from the repositioned `fields` map, used
 * to order same-page items top-to-bottom.
 *
 * The geometry is only present on the GetIngestionDocument fragment (the side
 * panel / extension); other fragments (e.g. the overview's External document)
 * omit it. When absent we return an empty lookup — the within-page tiebreak
 * becomes a no-op and items keep page-level ordering. We never fetch geometry
 * that isn't already on the document.
 */
function buildFieldPositionLookup(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment
): FieldPositionLookup {
  const lookup: FieldPositionLookup = new Map();
  const fields =
    'fields' in document
      ? (document.fields as Record<string, unknown> | null | undefined)
      : undefined;

  if (!fields) return lookup;

  for (const [contractDocumentUuid, rawFields] of Object.entries(fields)) {
    if (!Array.isArray(rawFields)) continue;

    const positionsByMappingKey = new Map<string, FieldPosition[]>();
    for (const rawField of rawFields as Array<{
      mappingKey?: unknown;
      pageNumber?: unknown;
      y?: unknown;
    }>) {
      const { mappingKey, pageNumber, y } = rawField;
      if (
        typeof mappingKey !== 'string' ||
        typeof pageNumber !== 'number' ||
        typeof y !== 'number'
      ) {
        continue;
      }

      const positions = positionsByMappingKey.get(mappingKey) ?? [];
      positions.push({ pageNumber, y });
      positionsByMappingKey.set(mappingKey, positions);
    }

    lookup.set(contractDocumentUuid, positionsByMappingKey);
  }

  return lookup;
}

/**
 * Vertical position of an item's topmost field on `page`, used to order items
 * within a single page (smaller = higher = first).
 *
 * Returns +Infinity ("sort last within the page") for items we can't place:
 * multi-page items, document-level items with no field, and items whose fields
 * aren't in the lookup (e.g. fragments without geometry). Best-effort by
 * design — it only refines ordering where the position already exists.
 */
function getWithinPageSortValue(
  item: ValidationItem,
  page: number,
  lookup: FieldPositionLookup
): number {
  if (item.pageNumbers?.length !== 1) return Number.POSITIVE_INFINITY;
  if (!item.contractDocumentUuid || !item.mappingKeys?.length) {
    return Number.POSITIVE_INFINITY;
  }

  const positionsByMappingKey = lookup.get(item.contractDocumentUuid);
  if (!positionsByMappingKey) return Number.POSITIVE_INFINITY;

  let topY = Number.POSITIVE_INFINITY;
  for (const mappingKey of item.mappingKeys) {
    const positions = positionsByMappingKey.get(mappingKey);
    if (!positions) continue;

    for (const position of positions) {
      if (position.pageNumber === page && position.y < topY) {
        topY = position.y;
      }
    }
  }

  return topY;
}

/**
 * Orders validation items so the list walks the ingestion document one contract
 * document at a time, in three keys:
 *
 * 1. The item's contract document's position in the combined PDF, so a
 *    multi-document PDF never interleaves two forms' items — which would render
 *    as page numbers restarting mid-list, since each item shows its
 *    document-relative page (BLO-13411).
 * 2. The item's first document-relative page, matching the on-screen label.
 * 3. Its topmost field's vertical position when geometry is available, so
 *    same-page items read top-to-bottom. This key stays on ABSOLUTE pages —
 *    the geometry lookup is built from the PDF's own page numbers.
 *
 * Multi-page and position-less items fall last within their page. Stable: items
 * that tie on all three keys keep their original relative order.
 */
function sortValidationItemsByPage(
  items: ValidationItem[],
  fieldPositionLookup: FieldPositionLookup,
  contractDocumentOrder: Map<string, number>
): ValidationItem[] {
  return [...items].sort((a, b) => {
    const aDoc = getContractDocumentSortValue(a, contractDocumentOrder);
    const bDoc = getContractDocumentSortValue(b, contractDocumentOrder);
    if (aDoc !== bDoc) return aDoc - bDoc;

    const aPage = getFirstDocumentPageNumber(a);
    const bPage = getFirstDocumentPageNumber(b);
    if (aPage !== bPage) return aPage - bPage;

    const aY = getWithinPageSortValue(
      a,
      getFirstPageNumber(a),
      fieldPositionLookup
    );
    const bY = getWithinPageSortValue(
      b,
      getFirstPageNumber(b),
      fieldPositionLookup
    );
    if (aY === bY) return 0;

    return aY - bY;
  });
}

/**
 * Orders an ingestion document's contract documents by where each first appears
 * in the combined PDF, so the per-document sections/tabs on the compliance
 * surfaces follow the bundle instead of the `contractDocuments` relation's
 * arbitrary database order.
 *
 * Contract documents with no known page range keep their original relative
 * order at the end (the sort is stable), so an unclassified or partially mapped
 * document is never dropped or hoisted.
 */
export function sortContractDocumentsByPageOrder<T extends { id: string }>(
  contractDocuments: readonly T[],
  contractDocumentPages?: Record<string, unknown> | null
): T[] {
  const order = getContractDocumentPageOrder(contractDocumentPages);
  if (order.size === 0) return [...contractDocuments];

  return [...contractDocuments].sort((a, b) => {
    const aOrder = order.get(a.id) ?? Number.POSITIVE_INFINITY;
    const bOrder = order.get(b.id) ?? Number.POSITIVE_INFINITY;
    if (aOrder === bOrder) return 0;

    return aOrder - bOrder;
  });
}

/**
 * The fields a check points at: its rule's focus fields, else the formula's
 * field inputs, else its single field.
 */
export const getValidationResultMappingKeys = (
  result: Pick<
    ValidationResult,
    'fieldMappingKey' | 'focusFieldMappingKeys' | 'inputValues'
  >
): string[] =>
  result.focusFieldMappingKeys ??
  result.inputValues?.flatMap(({ variableSource, fieldKey }) =>
    variableSource === VariableSource.FIELD ? [fieldKey] : []
  ) ??
  (result.fieldMappingKey ? [result.fieldMappingKey] : []);

/**
 * Extracts validation results for a specific document.
 * Error items use validated display hints when they narrow the existing focus
 * fields; successful items retain their full field set. When loaded geometry
 * makes a hint unrenderable, use an available authored fallback on the same pages.
 *
 * @param document - The ingestion document to get validation results for
 * @param validationData - Optional validation data containing document validations
 * @param contractDocumentId - Optional specific contract document ID to filter results
 * @param hiddenDocumentValidationReviews - Optional review records marking
 *   errors as hidden. Any error matching a hidden review (same `ruleUuid`,
 *   `contractDocumentUuid`, and `ingestionDocumentValidation.id`) is removed
 *   from the returned `errors`, mirroring
 *   {@link getErrorAndSuccessCountsForContractDocument}.
 * @returns Object containing arrays of error, success, and org-ignored validation items
 *
 * @example
 * const { errors, successes } = getValidationResultsForDocument(
 *   document,
 *   validationData,
 *   'contract-123'
 * );
 * Log.info(`Found ${errors.length} errors and ${successes.length} successes`);
 */
export function getValidationResultsForDocument(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment,
  validationData?: ComplianceValidationReportData,
  contractDocumentId?: string,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): ValidationResultsForDocument {
  if (!document || !validationData?.documentValidations) {
    return { errors: [], successes: [], organizationIgnoredRules: [] };
  }

  // Get contract document UUIDs from the document
  const contractDocumentUuids = new Set(
    document.contractDocuments?.map((cd: { id: string }) => cd.id) || []
  );

  if (contractDocumentUuids.size === 0) {
    return { errors: [], successes: [], organizationIgnoredRules: [] };
  }

  // Per-contract-document page footprints, used to renumber each item's failing
  // pages for display. Read defensively: the field is a JSON scalar and is not
  // present on every document fragment this helper accepts.
  const contractDocumentPages = (
    document as {
      contractDocumentPages?: Record<string, unknown> | null;
    }
  ).contractDocumentPages;

  const errors: ValidationItem[] = [];
  const successes: ValidationItem[] = [];
  const organizationIgnoredRules: ValidationItem[] = [];
  const errorIds = new Set<string>();
  const successIds = new Set<string>();
  const organizationIgnoredRuleIds = new Set<string>();
  let highlightFields: FieldsByContractDocument | undefined;

  // Process each document validation
  validationData.documentValidations.forEach(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      // Handle the dynamic Record structure from GraphQL
      const aiValidationResults =
        docValidation.aiValidationResults as unknown as
          | ValidationResult[]
          | undefined;
      const validationResults = docValidation.validationResults as unknown as
        | ValidationResult[]
        | undefined;

      // Prioritize AI validation results, but fall back to regular validation results if AI results are empty
      const resultsToProcess =
        aiValidationResults && aiValidationResults.length > 0
          ? aiValidationResults
          : validationResults;

      if (resultsToProcess) {
        resultsToProcess.forEach((result) => {
          // Only include results for this document's contract documents
          if (
            result.contractDocumentUuid &&
            contractDocumentUuids.has(result.contractDocumentUuid)
          ) {
            // If contractDocumentId is provided, filter by specific contract document
            if (
              contractDocumentId &&
              result.contractDocumentUuid !== contractDocumentId
            ) {
              return;
            }

            const fallbackMappingKeys = getValidationResultMappingKeys(result);

            // The JSON scalar may contain old or malformed hints. A hint can
            // only narrow an error's existing field set, never add new fields.
            const parsedHighlights =
              validationFieldHighlightsSchema.safeParse(result);
            if (!parsedHighlights.success) {
              Sentry.captureMessage(
                'Ignored malformed validation field highlights',
                {
                  level: 'warning',
                  extra: {
                    ruleUuid: result.ruleUuid,
                    issueCount: parsedHighlights.error.issues.length,
                  },
                }
              );
            }
            const highlightKeys = parsedHighlights.success
              ? parsedHighlights.data.highlightFieldMappingKeys
              : undefined;
            const explanation =
              validationFailureExplanationSchema.safeParse(result);
            if (!explanation.success) {
              Sentry.captureMessage(
                'Ignored malformed validation explanation',
                {
                  level: 'warning',
                  extra: {
                    ruleUuid: result.ruleUuid,
                    issueCount: explanation.error.issues.length,
                  },
                }
              );
            }
            const fallbackKeySet = new Set(fallbackMappingKeys);
            const absolutePageNumbers = getPageNumbers(result);
            let mappingKeys = fallbackMappingKeys;
            if (
              result.output &&
              highlightKeys &&
              highlightKeys.every((key) => fallbackKeySet.has(key))
            ) {
              mappingKeys = highlightKeys;
              highlightFields ??= parseFieldsByContractDocument(
                'fields' in document ? document.fields : undefined
              );
              const scope = {
                contractDocumentUuid: result.contractDocumentUuid,
                pageNumbers: absolutePageNumbers,
              };
              // Decide once for PDF rendering, extension overlays and click targets.
              if (
                getValidationItemFieldIds(
                  { ...scope, mappingKeys },
                  highlightFields
                ).length === 0 &&
                getValidationItemFieldIds(
                  { ...scope, mappingKeys: fallbackMappingKeys },
                  highlightFields
                ).length > 0
              ) {
                mappingKeys = fallbackMappingKeys;
              }
            }

            // Ensure inputValues have properly serialized values
            const normalizedInputValues = result.inputValues?.map((input) => {
              let normalizedValue: string;
              if (typeof input.value === 'string') {
                normalizedValue = input.value;
              } else if (
                typeof input.value === 'object' &&
                input.value !== null
              ) {
                // GraphQL sometimes returns objects instead of JSON strings
                normalizedValue = JSON.stringify(input.value);
              } else {
                normalizedValue = String(input.value);
              }

              return {
                ...input,
                value: normalizedValue,
              };
            });

            const pageRange = getContractDocumentPageRange(
              contractDocumentPages,
              result.contractDocumentUuid
            );

            const item: ValidationItem = {
              message: result.message,
              ruleUuid: result.ruleUuid,
              ruleType: result.ruleType,
              pageNumbers: absolutePageNumbers,
              documentPageNumbers: toDocumentRelativePages(
                absolutePageNumbers,
                pageRange
              ),
              documentPageCount: getContractDocumentPageCount(pageRange),
              contractDocumentUuid: result.contractDocumentUuid,
              mappingKeys,
              highlightFieldMappingKeys:
                result.output && mappingKeys === highlightKeys
                  ? highlightKeys
                  : undefined,
              canFilterHoverFields:
                result.output &&
                mappingKeys === highlightKeys &&
                parsedHighlights.success &&
                parsedHighlights.data.canFilterHoverFields === true,
              failureExplanation:
                result.output && explanation.success
                  ? explanation.data.failureExplanation
                  : undefined,
              inputValues: normalizedInputValues,
              ingestionDocumentValidationId: docValidation.id,
              ruleFormula: result.ruleFormula,
              isDocumentValidation: result.isDocumentValidation,
              isPipelineValidation: result.isPipelineValidation,
              isOrganizationRule: result.isOrganizationRule,
              isOrganizationIgnored: result.isOrganizationIgnored,
            };

            // De-dupe per CONTRACT DOCUMENT, not per rule. Results are stored
            // twice, so exact repeats within one contract document must still
            // collapse — but the same rule failing on two contract documents is
            // two distinct findings on different pages, and keying on `ruleUuid`
            // alone discarded the second. That cost the extension its red overlay
            // for the second document (the surviving item's pages also drive the
            // overlay filter) and dropped a line from the agent's resubmit note.
            //
            // Only the callers that omit `contractDocumentId` ever saw the loss;
            // for the per-contract-document callers every item shares one uuid,
            // so this key behaves exactly as the old one did.
            //
            // A falsy `ruleUuid` maps to '' so the add-guards below keep their
            // current behaviour verbatim: errors/successes never de-dupe such
            // items (their `.add` is guarded), org-ignored ones still do.
            const dedupeKey = item.ruleUuid
              ? `${item.contractDocumentUuid ?? ''}|${item.ruleUuid}`
              : '';

            if (item.isOrganizationIgnored) {
              if (!organizationIgnoredRuleIds.has(dedupeKey)) {
                organizationIgnoredRules.push(item);
                organizationIgnoredRuleIds.add(dedupeKey);
              }

              return;
            }

            if (result.output && !errorIds.has(dedupeKey)) {
              // Drop errors hidden by a review (same rule + contract document +
              // validation version), mirroring the count helper's matching.
              const isHidden = hiddenDocumentValidationReviews?.some(
                (review) =>
                  review.ruleUuid === item.ruleUuid &&
                  review.contractDocumentUuid === item.contractDocumentUuid &&
                  review.ingestionDocumentValidation?.id ===
                    item.ingestionDocumentValidationId &&
                  review.isHidden
              );

              if (!isHidden) {
                errors.push(item);

                if (item.ruleUuid) {
                  errorIds.add(dedupeKey);
                }
              }
            }

            if (!result.output && !successIds.has(dedupeKey)) {
              successes.push(item);

              if (item.ruleUuid) {
                successIds.add(dedupeKey);
              }
            }
          }
        });
      }
    }
  );

  const fieldPositionLookup = buildFieldPositionLookup(document);
  // Groups items by contract document before page, which matters on the flat
  // surfaces (extension, agent review) that ask for every contract document at
  // once. When callers pass a `contractDocumentId` the list is already
  // single-document, so this key just ties.
  const contractDocumentOrder = getContractDocumentPageOrder(
    contractDocumentPages
  );

  return {
    errors: Object.values(
      groupBy(
        sortValidationItemsByPage(
          errors,
          fieldPositionLookup,
          contractDocumentOrder
        ),
        'contractDocumentUuid'
      )
    ).flatMap((items) =>
      partition(items, (item) => item.ruleType === RuleType.ESCALATE).flat()
    ),
    successes: sortValidationItemsByPage(
      successes,
      fieldPositionLookup,
      contractDocumentOrder
    ),
    organizationIgnoredRules: sortValidationItemsByPage(
      organizationIgnoredRules,
      fieldPositionLookup,
      contractDocumentOrder
    ),
  };
}

/**
 * Gets error and success counts for a specific contract document.
 * Escalation checks remain visible but do not contribute to these counters.
 *
 * This function filters out hidden errors based on the following matching rules:
 * - A review hides an error when ALL of these conditions are met:
 *   1. review.ruleUuid matches the error's ruleUuid
 *   2. review.contractDocumentUuid equals the provided contractDocumentUuid
 *   3. review.ingestionDocumentValidation.id equals the current validation's id (validation-version-specific hide)
 *   4. review.isHidden is true
 *
 * @param contractDocumentUuid - The UUID of the contract document to count errors for
 * @param validationData - The validation data containing document validations and results
 * @param hiddenDocumentValidationReviews - Optional array of review records marking errors as hidden
 * @returns Object containing counts of { errors, successes }
 */
function getErrorAndSuccessCountsForContractDocument(
  contractDocumentUuid: string,
  validationData: ComplianceValidationReportData,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): { errors: number; successes: number } {
  if (!validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  let errorCount = 0;
  let successCount = 0;

  validationData.documentValidations.forEach(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      const aiValidationResults =
        docValidation.aiValidationResults as unknown as
          | ValidationResult[]
          | undefined;
      const validationResults = docValidation.validationResults as unknown as
        | ValidationResult[]
        | undefined;

      const resultsToProcess =
        aiValidationResults && aiValidationResults.length > 0
          ? aiValidationResults
          : validationResults;

      if (resultsToProcess) {
        resultsToProcess.forEach((result) => {
          if (result.contractDocumentUuid === contractDocumentUuid) {
            if (
              result.isOrganizationIgnored ||
              result.ruleType === RuleType.ESCALATE
            ) {
              return;
            }

            if (result.output) {
              // Check if this error is hidden based on matching ruleUuid, contractDocumentUuid, and validation ID
              const isHidden = hiddenDocumentValidationReviews?.some(
                (review) =>
                  review.ruleUuid === result.ruleUuid &&
                  review.contractDocumentUuid === contractDocumentUuid &&
                  review.ingestionDocumentValidation?.id === docValidation.id &&
                  review.isHidden
              );

              // Only count if not hidden
              if (!isHidden) {
                errorCount++;
              }
            } else {
              successCount++;
            }
          }
        });
      }
    }
  );

  return { errors: errorCount, successes: successCount };
}

/**
 * Which fragments of an ingestion document a count covers.
 *
 * A named case per question, rather than two optional arguments that both
 * narrowed the same dimension: `contractDocumentId` and
 * `inScopeContractDocumentUuids` could previously be passed together, and the
 * single-fragment one silently won. It also retires the "absent means
 * everything" convention, which had to be documented at each layer that
 * forwarded it because it was expressed as a missing optional rather than a
 * case with a name.
 */
export type IngestionDocumentCountScope =
  /** Every fragment — the document- and folder-level question. */
  | { kind: 'WHOLE_DOCUMENT' }
  /** One fragment, addressed directly. */
  | { kind: 'FRAGMENT'; contractDocumentUuid: string }
  /**
   * Only these fragments — the set a SkySlope checklist item's rule actually
   * required (BLO-14871).
   */
  | { kind: 'IN_SCOPE'; contractDocumentUuids: ReadonlySet<string> };

/**
 * Gets error and success counts for an ingestion document, over the fragments
 * `scope` selects.
 *
 * Takes an options object because the positional form had five parameters with
 * two adjacent optionals — the shape where an argument lands in the wrong slot
 * without a type error.
 */
export function getErrorAndSuccessCountsForIngestionDocument(input: {
  document: {
    contractDocuments?:
      | readonly {
          id: string;
        }[]
      | null;
  };
  validationData: ComplianceValidationReportData;
  scope: IngestionDocumentCountScope;
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
}): { errors: number; successes: number } {
  const { document, validationData, scope, hiddenDocumentValidationReviews } =
    input;

  if (!document.contractDocuments || !validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  if (scope.kind === 'FRAGMENT') {
    return getErrorAndSuccessCountsForContractDocument(
      scope.contractDocumentUuid,
      validationData,
      hiddenDocumentValidationReviews
    );
  }

  // Narrowing here rather than inside the loop reduces work: each fragment
  // otherwise rescans every validation in the folder.
  const contractDocuments =
    scope.kind === 'WHOLE_DOCUMENT'
      ? document.contractDocuments
      : document.contractDocuments.filter((contractDoc) =>
          scope.contractDocumentUuids.has(contractDoc.id)
        );

  let totalErrorCount = 0;
  let totalSuccessCount = 0;

  contractDocuments.forEach((contractDoc) => {
    const counts = getErrorAndSuccessCountsForContractDocument(
      contractDoc.id,
      validationData,
      hiddenDocumentValidationReviews
    );
    totalErrorCount += counts.errors;
    totalSuccessCount += counts.successes;
  });

  return { errors: totalErrorCount, successes: totalSuccessCount };
}

/**
 * Converts a field key into a human-readable label.
 *
 * Transforms nested field keys by replacing structural characters with readable separators
 * and applying title case formatting.
 *
 * @param key - The field key to transform (e.g., "buyer[0].name_first")
 * @returns A human-readable label (e.g., "Buyer > 0 Name | First")
 *
 * @example
 * friendlifyLabel("buyer[0].name_first") // "Buyer > 0 Name | First"
 * friendlifyLabel("seller.address_street") // "Seller | Address Street"
 */
const friendlifyLabel = (key: string) => {
  const replacedKey = key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replaceAll(']', ' ')
    .replaceAll('_', ' ')
    .replaceAll('.', ' | ')
    .replaceAll('[', ' > ')
    .replaceAll('  ', ' ');

  return titleCase(replacedKey, {
    titleTerminators: new Set([...TITLE_TERMINATORS, '|', '>']),
  });
};

/**
 * Groups and formats input values for display in tooltips
 */

/**
 * Strips -repositioned suffix from a fieldUuid if present
 */
function cleanFieldUuid(fieldUuid: string): string {
  return fieldUuid.replace(/-repositioned$/, '');
}

/**
 * Type guard to check if an unknown value is a MultiFieldValue
 */
function isMultiFieldValue(value: unknown): value is MultiFieldValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'fieldUuid' in value &&
    'value' in value &&
    typeof value.fieldUuid === 'string' &&
    typeof value.value === 'string'
  );
}

/**
 * Type guard to check if an unknown value is a MultiFieldValue array
 */
function isMultiFieldValueArray(value: unknown): value is MultiFieldValue[] {
  return (
    Array.isArray(value) && value.length > 0 && isMultiFieldValue(value[0])
  );
}

/**
 * Extracts field UUIDs from a MultiFieldValue array value
 * Returns array of UUIDs if the value is a valid MultiFieldValue array, null otherwise
 */
export function extractFieldUuidsFromValue(value: unknown): string[] | null {
  try {
    let parsed: unknown;

    // If value is already an object/array, use it directly
    if (typeof value === 'object' && value !== null) {
      parsed = value;
    } else if (typeof value === 'string') {
      parsed = JSON.parse(value);
    } else {
      return null;
    }

    if (isMultiFieldValueArray(parsed)) {
      // Strip -repositioned suffix from field UUIDs as a safety measure
      return parsed.map((item) => cleanFieldUuid(item.fieldUuid));
    }
  } catch (e) {
    // Not JSON or not a MultiFieldValue array
  }

  return null;
}

const tooltipFieldTypeSchema = z.enum(FieldType);

const tooltipFieldValuesSchema = z.array(
  z.object({
    fieldUuid: z.string(),
    value: z.string(),
    pageNumber: z.number().int().positive().optional(),
  })
);

const tooltipPageCountsSchema = z
  .array(
    z.object({
      page: z.number().int().positive(),
      count: z.number().int().nonnegative(),
    })
  )
  .min(1)
  .refine(
    (counts) => new Set(counts.map(({ page }) => page)).size === counts.length
  );

export interface ValidationTooltipInput {
  fieldKey: string;
  label: string;
  value: string;
  fieldType?: FieldType;
  sourceLabel?: string;
  pageCounts?: Array<{ page: number; count: number }>;
  fieldValues?: Array<{
    fieldUuid: string;
    value: string;
    pageNumber?: number;
  }>;
}

/** Preserves the original tooltip values while the popover is being rolled out. */
export function formatLegacyInputValuesForTooltip(
  inputValues: ValidationItem['inputValues'],
  document?: Pick<GetIngestionDocumentFragment, 'contractDocuments'> | null
): {
  fromContract: Array<{ label: string; value: string }>;
  externalReference: Array<{ label: string; value: string }>;
} {
  const labels = createFieldKeyToLabelMapping(document);
  const fromContract: Array<{ label: string; value: string }> = [];
  const externalReference: Array<{ label: string; value: string }> = [];

  for (const input of inputValues ?? []) {
    if (input.fieldKey === 'ingestion.documentFormNames') continue;
    const isLocal =
      (input.variableSource ?? VariableSource.FIELD) === VariableSource.FIELD;
    if (isLocal && !labels[input.fieldKey]) continue;
    let value = !input.value || input.value === 'undefined' ? '-' : input.value;
    try {
      const parsed: unknown = JSON.parse(input.value);
      if (isMultiFieldValueArray(parsed)) {
        const statuses = parsed
          .map((field, index) => {
            const status = field.value ? 'Signed' : 'Unsigned';

            return parsed.length === 1 ? status : `${index + 1}: ${status}`;
          })
          .join(', ');
        value = statuses || value;
      }
    } catch {
      value = !input.value || input.value === 'undefined' ? '-' : input.value;
    }
    const item = {
      label: labels[input.fieldKey] || friendlifyLabel(input.fieldKey),
      value,
    };
    (isLocal ? fromContract : externalReference).push(item);
  }

  return { fromContract, externalReference };
}

function formatTooltipScalar(
  value: string,
  fieldType?: FieldType | null
): string {
  if (value === 'undefined' || value === 'null') return 'Unavailable';

  return match(fieldType)
    .with(FieldType.CHECKBOX, FieldType.RADIO, () =>
      value === 'true'
        ? 'Selected'
        : value === 'false'
          ? 'Not selected'
          : 'Unavailable'
    )
    .with(FieldType.INITIAL, FieldType.SIGNATURE, () =>
      value === '' ? 'Unsigned' : 'Signed'
    )
    .otherwise(() => (value === '' ? 'Empty' : value));
}

function parseMultiFieldValue(
  value: string,
  fieldType?: FieldType | null
): Pick<ValidationTooltipInput, 'value' | 'fieldValues'> | null {
  try {
    const parsed: unknown = JSON.parse(value);
    const fieldValues = tooltipFieldValuesSchema.safeParse(parsed);
    if (fieldValues.success) {
      if (fieldValues.data.length === 0) return { value: 'Unavailable' };

      return {
        fieldValues: fieldValues.data,
        value: fieldValues.data
          .map((item, index) => {
            const status = formatTooltipScalar(item.value, fieldType);
            const position =
              item.pageNumber != null
                ? `Template page ${item.pageNumber}`
                : `Field ${index + 1}`;

            return fieldValues.data.length === 1 && item.pageNumber == null
              ? status
              : `${position}: ${status}`;
          })
          .join('\n'),
      };
    }
    if (
      Array.isArray(parsed) ||
      (typeof parsed === 'object' && parsed !== null)
    ) {
      const list = z
        .array(z.union([z.string(), z.number(), z.boolean()]))
        .safeParse(parsed);

      return {
        value:
          list.success && list.data.length > 0
            ? list.data.map(String).join('\n')
            : 'Unavailable',
      };
    }
  } catch {
    return null;
  }

  return null;
}

/** Formats saved inputs and returns their parsed fields without reevaluating rules. */
export function formatInputValuesForTooltip(
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[],
  document?:
    | (Pick<GetIngestionDocumentFragment, 'contractDocuments'> & {
        fields?: unknown;
      })
    | null,
  variant: 'internal' | 'external' = 'external',
  contractDocumentUuid?: string
): {
  fromContract: ValidationTooltipInput[];
  externalReference: ValidationTooltipInput[];
  fieldsByDocument: FieldsByContractDocument;
} {
  if (!inputValues || inputValues.length === 0) {
    return { fromContract: [], externalReference: [], fieldsByDocument: {} };
  }

  const fieldKeyToLabel = createFieldKeyToLabelMapping(
    document,
    contractDocumentUuid
  );
  const fieldsByDocument = parseFieldsByContractDocument(document?.fields);
  const fields = contractDocumentUuid
    ? fieldsByDocument[contractDocumentUuid] ?? []
    : Object.values(fieldsByDocument).flat();
  const fieldsByKey = groupBy(fields, 'mappingKey');
  const referenceLabels = memoize((documentId: string) =>
    createFieldKeyToLabelMapping(document, documentId)
  );

  const fromContract: ValidationTooltipInput[] = [];
  const externalReference: ValidationTooltipInput[] = [];

  const shouldShowInputValue = (fieldKey: string) => {
    if (fieldKey === 'ingestion.documentFormNames') {
      return variant === 'internal';
    }

    return true;
  };

  inputValues.forEach((input) => {
    if (!shouldShowInputValue(input.fieldKey)) {
      return;
    }
    const source = input.variableSource ?? VariableSource.FIELD;
    const isLocal =
      source === VariableSource.FIELD ||
      source === VariableSource.SIGNATURE_GROUP;
    const prefix = input.fieldKey.split('.')[0];
    const referenceKey =
      !isLocal && input.fieldKey.includes('.')
        ? input.fieldKey.slice(prefix.length + 1)
        : input.fieldKey;
    const referenceDocument =
      source === VariableSource.DOCUMENT
        ? document?.contractDocuments.find(
            ({ documentVersion }) => documentVersion?.document.form === prefix
          )
        : undefined;
    const matchingFields = isLocal
      ? fieldsByKey[input.fieldKey] ?? []
      : referenceDocument
        ? (fieldsByDocument[referenceDocument.id] ?? []).filter(
            ({ mappingKey }) => mappingKey === referenceKey
          )
        : [];
    const fieldTypes = new Set(
      matchingFields.map(({ fieldType }) => {
        const parsed = tooltipFieldTypeSchema.safeParse(fieldType);

        return parsed.success ? parsed.data : undefined;
      })
    );
    const signatureKind = input.fieldKey.match(
      /(?:^|\.)SIGNER\[[^\]]+\]\[(signature|initial)\]$/
    )?.[1];
    const choiceKind = referenceKey.match(/^(radio|checkbox)\[/)?.[1];
    const fieldType =
      matchingFields.length > 0
        ? fieldTypes.size === 1
          ? [...fieldTypes][0]
          : undefined
        : match(signatureKind)
            .with('signature', () => FieldType.SIGNATURE)
            .with('initial', () => FieldType.INITIAL)
            .otherwise(() =>
              match(choiceKind)
                .with('radio', () => FieldType.RADIO)
                .with('checkbox', () => FieldType.CHECKBOX)
                .otherwise(() => undefined)
            );
    const labels = new Set(
      matchingFields
        .map(({ label }) => (typeof label === 'string' ? label : ''))
        .filter(Boolean)
    );
    const mappedLabel = isLocal
      ? fieldKeyToLabel[input.fieldKey]
      : referenceDocument
        ? referenceLabels(referenceDocument.id)[referenceKey]
        : undefined;
    const friendlyLabel =
      mappedLabel ||
      (labels.size === 1 ? [...labels][0] : undefined) ||
      friendlifyLabel(input.fieldKey);

    // Value is guaranteed to be a string from getValidationResultsForDocument
    const stringValue = input.value;

    // Try to parse as MultiFieldValue array and format it for display
    const multiFieldDisplay = parseMultiFieldValue(stringValue, fieldType);

    const friendlyValue =
      multiFieldDisplay?.value ?? formatTooltipScalar(stringValue, fieldType);

    const displayItem: ValidationTooltipInput = {
      fieldKey: referenceKey,
      label: friendlyLabel,
      value: friendlyValue,
      fieldType,
      fieldValues: multiFieldDisplay?.fieldValues,
    };

    if (source === VariableSource.SIGNATURE_GROUP) {
      displayItem.fieldType = undefined;
      displayItem.fieldValues = undefined;
      try {
        const rawCounts: unknown = JSON.parse(stringValue);
        const counts = tooltipPageCountsSchema.safeParse(rawCounts);
        const legacyCounts = z
          .array(z.number().int().nonnegative())
          .min(1)
          .safeParse(rawCounts);
        if (counts.success) {
          displayItem.pageCounts = counts.data.sort(
            (first, second) => first.page - second.page
          );
          displayItem.value = `${counts.data.length} mapped template pages`;
        } else {
          displayItem.value = legacyCounts.success
            ? legacyCounts.data
                .map((count, index) => `Entry ${index + 1}: ${count} marks`)
                .join('\n')
            : 'Unavailable';
        }
      } catch {
        displayItem.value = 'Unavailable';
      }
    }

    if (isLocal) {
      fromContract.push(displayItem);
    } else {
      displayItem.sourceLabel = match(source)
        .with(VariableSource.DOCUMENT, () =>
          input.fieldKey.includes('.') ? `Form ${prefix}` : 'Reference document'
        )
        .with(VariableSource.INDIGO_OFFER, () =>
          match(prefix)
            .with('property', () => 'Property reference')
            .with('mlsListing', () => 'MLS listing')
            .with('latestSale', () => 'Latest sale')
            .otherwise(() => 'Offer reference')
        )
        .with(VariableSource.OFFER_INGESTION, () => 'Transaction')
        .with(
          VariableSource.OFFER_INGESTION_DOCUMENT,
          () => 'Uploaded document'
        )
        .with(VariableSource.SELF, () => 'Rule context')
        .otherwise(() => 'Reference data');
      if (
        input.fieldKey.includes('.') &&
        (source === VariableSource.DOCUMENT ||
          (source === VariableSource.INDIGO_OFFER &&
            ['property', 'mlsListing', 'latestSale'].includes(prefix)))
      ) {
        displayItem.label = mappedLabel || friendlifyLabel(referenceKey);
      }
      externalReference.push(displayItem);
    }
  });

  return { fromContract, externalReference, fieldsByDocument };
}

export interface ImprovedField {
  fieldKey: string;
  fieldLabel: string;
  reductionCount: number;
}

export interface AnalysisWithFields {
  analysis: string;
  contractDocumentUuid: string;
  relatedFieldKeys?: string[];
}

export interface ValidationComparisonData {
  aiErrorCount: number;
  regularErrorCount: number;
  aiWarningCount: number;
  regularWarningCount: number;
  reductionPercentage: number;
  hasAiData: boolean;
  latestValidationDate?: string;
  aiAnalysisData?: string[];
  improvedFieldsList?: ImprovedField[];
  aiAnalysisWithFields?: AnalysisWithFields[];
}

/**
 * Calculates percentage reduction in validation issues
 */
function calculateReductionPercentage(
  totalRegularIssues: number,
  totalAiIssues: number
): number {
  if (totalRegularIssues === 0) return 0;

  return Math.round(
    ((totalRegularIssues - totalAiIssues) / totalRegularIssues) * 100
  );
}

/**
 * Extracts fields that were improved by AI processing
 */
function extractImprovedFields(
  fieldComparisons: Array<{
    fieldKey: string;
    fieldLabel?: string;
    changeType: string;
    reductionCount: number;
  }>
): ImprovedField[] {
  return fieldComparisons
    .filter(
      (field) =>
        field.changeType === 'removed' ||
        (field.changeType === 'modified' && field.reductionCount > 0)
    )
    .map((field) => ({
      fieldKey: field.fieldKey,
      fieldLabel: field.fieldLabel || field.fieldKey,
      reductionCount: field.reductionCount,
    }));
}

/**
 * Associates AI analysis insights with related field keys
 */
function buildAnalysisWithFields(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation,
  fieldComparisons: Array<{
    fieldKey: string;
    changeType: string;
    reductionCount: number;
    aiResults: ValidationResult[];
  }>
): AnalysisWithFields[] {
  const analysisWithFields: AnalysisWithFields[] = [];

  const analyses = validationForDocument.aiAnalysis as
    | Array<{
        contractDocumentUuid: string;
        analysis: string;
      }>
    | undefined;

  if (!analyses) return [];

  analyses.forEach((analysisItem) => {
    const relatedFieldKeys = fieldComparisons
      .filter(
        (field) =>
          (field.changeType === 'removed' ||
            (field.changeType === 'modified' && field.reductionCount > 0)) &&
          field.aiResults.some(
            (result) =>
              result.contractDocumentUuid === analysisItem.contractDocumentUuid
          )
      )
      .map((field) => field.fieldKey);

    analysisWithFields.push({
      analysis: analysisItem.analysis,
      contractDocumentUuid: analysisItem.contractDocumentUuid,
      relatedFieldKeys:
        relatedFieldKeys.length > 0 ? relatedFieldKeys : undefined,
    });
  });

  return analysisWithFields;
}

/**
 * Gets the validation for a specific IngestionDocument.
 */
export function getValidationForDocument(
  document: GetIngestionDocumentFragment,
  validationData?: ComplianceValidationReportData
): ComplianceIngestionDocumentValidation | null {
  if (!validationData?.documentValidations) {
    return null;
  }

  // Absent when the query left the report's details out (only the SkySlope
  // embed does, and it never calls this): then no validation matches.
  const validations = validationData.documentValidations.filter(
    (validation) => document.id === validation.ingestionDocument?.id
  );

  if (validations.length === 0) {
    return null;
  }

  if (validations.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`Multiple validations found for document ${document.id}.`);
  }

  return validations[0];
}

/**
 * Compares AI vs regular validation results for a specific document
 */
export function getValidationComparisonData(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation
): ValidationComparisonData | null {
  const comparisonData: ValidationComparisonData = {
    aiErrorCount: 0,
    aiWarningCount: 0,
    regularErrorCount: 0,
    regularWarningCount: 0,
    reductionPercentage: 0,
    hasAiData: false,
  };

  let hasAiProcessing = false;
  let mostRecentValidationDate: string | undefined;

  if (!!validationForDocument.aiValidationResults?.length) {
    hasAiProcessing = true;
  }

  if (validationForDocument.updatedAt) {
    mostRecentValidationDate = validationForDocument.updatedAt as string;
  }

  comparisonData.aiErrorCount = validationForDocument.aiErrorCount;
  comparisonData.aiWarningCount = validationForDocument.aiWarningCount;
  comparisonData.regularErrorCount = validationForDocument.errorCount;
  comparisonData.regularWarningCount = validationForDocument.warningCount;

  const totalRegularIssues =
    comparisonData.regularErrorCount + comparisonData.regularWarningCount;
  const totalAiIssues =
    comparisonData.aiErrorCount + comparisonData.aiWarningCount;
  const improvementPercentage = calculateReductionPercentage(
    totalRegularIssues,
    totalAiIssues
  );

  const fieldComparisonData = getFieldLevelComparisonData(
    document,
    validationForDocument
  );
  const improvedFields = extractImprovedFields(
    fieldComparisonData.fieldComparisons
  );
  const analysisWithFieldContext = buildAnalysisWithFields(
    document,
    validationForDocument,
    fieldComparisonData.fieldComparisons
  );

  return {
    ...comparisonData,
    reductionPercentage: improvementPercentage,
    hasAiData: hasAiProcessing,
    latestValidationDate: mostRecentValidationDate,
    improvedFieldsList: improvedFields.length > 0 ? improvedFields : undefined,
    aiAnalysisWithFields:
      analysisWithFieldContext.length > 0
        ? analysisWithFieldContext
        : undefined,
  };
}

/**
 * Whether the displayed validation results include this ingestion document,
 * or its specific contract document when a focused card is being shown.
 */
export function hasDocumentBeenValidated(
  document: ExternalIngestionDocumentFragment,
  validationData: ComplianceValidationReportData,
  contractDocumentId?: string
): boolean {
  if (!validationData?.documentValidations) {
    return false;
  }

  return validationData.documentValidations.some(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      const aiResults = docValidation.aiValidationResults as unknown as
        | { ingestionDocumentUuid?: string; contractDocumentUuid?: string }[]
        | undefined;
      const regularResults = docValidation.validationResults as unknown as
        | { ingestionDocumentUuid?: string; contractDocumentUuid?: string }[]
        | undefined;
      const results = aiResults?.length ? aiResults : regularResults;

      return results?.some(
        (result) =>
          result.ingestionDocumentUuid === document.id &&
          (contractDocumentId === undefined ||
            result.contractDocumentUuid === contractDocumentId)
      );
    }
  );
}

/**
 * Whether an auto-approved document has a frozen decision-time validation.
 * External validation fragments cannot carry this internal-only field, so the
 * result remains false on customer surfaces without a separate variant flag.
 */
export function hasDecisionTimeValidationSnapshot(
  document: Pick<ExternalIngestionDocumentFragment, 'id' | 'wasAutoApproved'>,
  validationData: ComplianceValidationReportData
): boolean {
  if (!document.wasAutoApproved || !validationData) {
    return false;
  }

  return validationData.documentValidations.some(
    (validation: ComplianceIngestionDocumentValidation) =>
      'decisionTimeValidationSnapshot' in validation &&
      validation.isCurrent &&
      validation.ingestionDocument.id === document.id &&
      validation.decisionTimeValidationSnapshot?.decision ===
        ComplianceAutoApprovalAttemptDecision.APPROVE
  );
}

export function hasIngestionDocumentBeenClassified(
  document: ExternalIngestionDocumentFragment
): boolean {
  return (document.contractDocuments?.length || 0) > 0;
}

/**
 * Gets display name for a contract document
 *
 * @param contractDoc - The contract document to get display name for
 * @returns Formatted display name like "Form - Name" or fallback to category
 */
/**
 * The form code and human name of a contract document, kept separate so a
 * caller can render the code as its own chip. Mirrors the fallback order of
 * {@link getContractDocDisplayName}: a fragment classified only by category
 * has no code, and its category stands in for the name.
 */
export function getContractDocDisplayParts(contractDoc?: {
  documentCategory?: string | null;
  /**
   * Only `documentVersion` itself is optional. Once present, the schema
   * guarantees the rest: `DocumentVersionObject.document` is `DocumentObject!`
   * and its `form` / `name` are `String!`. Declaring those optional described
   * a shape the API cannot return.
   */
  documentVersion?: {
    document: {
      form: string;
      name: string;
    };
  } | null;
}): { formCode: string | null; formName: string } {
  const document = contractDoc?.documentVersion?.document;

  // Non-null per the schema, but `String!` guarantees non-null, not non-empty:
  // an empty form code would render a blank chip, so both must be truthy.
  if (document != null && document.form && document.name) {
    return { formCode: document.form, formName: document.name };
  }

  return {
    formCode: null,
    formName: contractDoc?.documentCategory || 'Unknown Document',
  };
}

export function getContractDocDisplayName(contractDoc?: {
  documentCategory?: string | null;
  /** See {@link getContractDocDisplayParts} on why only this level is optional. */
  documentVersion?: {
    document: {
      form: string;
      name: string;
    };
  } | null;
}): string {
  const { formCode, formName } = getContractDocDisplayParts(contractDoc);

  return formCode != null ? `${formCode} - ${formName}` : formName;
}
